import { apiRequest } from "./api";
import type { components } from "./api-types";

/**
 * v2.10 P3 (D-132, D-136): texting from the CRM. Templates and rules only ever fill a draft; a text goes
 * out only when Sam presses Send on the confirm step, which names the number and shows the final body.
 * T1 replies inside an existing Voice thread (sends then and there); T2 opens Voice web with the text on the
 * clipboard and Sam presses Send in Voice. Never an `sms:` link.
 */
type S = components["schemas"];
export type SmsTemplate = S["SmsTemplate"];
export type SmsMerged = S["SmsMerged"];
export type SmsPrepared = S["SmsPrepared"];
export type SmsSent = S["SmsSent"];
export type SmsDraftRow = S["SmsDraftRow"];
export type SmsOwner = { kind: "deal" | "lead"; id: string };

const post = <T,>(path: string, body: unknown) =>
  apiRequest<T>(path, { method: "POST", body: JSON.stringify(body) });

const target = (owner: SmsOwner) =>
  owner.kind === "lead" ? { lead_id: owner.id } : { deal_id: owner.id };

export const sms = {
  templates: (all = false) =>
    apiRequest<SmsTemplate[]>(`/api/v1/sms/templates${all ? "?include_archived=true" : ""}`),
  addTemplate: (body: { name: string; body: string }) => post<SmsTemplate>("/api/v1/sms/templates", body),
  saveTemplate: (id: string, body: { name: string; body: string; archived?: boolean }) =>
    apiRequest<SmsTemplate>(`/api/v1/sms/templates/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  merge: (owner: SmsOwner, input: { template_id?: string; body?: string }) =>
    post<SmsMerged>("/api/v1/sms/merge", { ...target(owner), ...input }),
  prepare: (owner: SmsOwner, phone: string, body: string) =>
    post<SmsPrepared>("/api/v1/sms/prepare", { ...target(owner), phone_e164: phone, body }),
  send: (
    owner: SmsOwner,
    input: { phone: string; body: string; token: string; outboxId?: string | null; templateId?: string | null },
  ) =>
    post<SmsSent>("/api/v1/sms/send", {
      ...target(owner),
      phone_e164: input.phone,
      body: input.body,
      confirm_token: input.token,
      outbox_id: input.outboxId ?? null,
      template_id: input.templateId ?? null,
    }),
  outbox: () => apiRequest<SmsDraftRow[]>("/api/v1/sms/outbox"),
  patchDraft: (id: string, body: { body?: string; dismiss?: boolean }) =>
    apiRequest<{ id: string; status: string }>(`/api/v1/sms/outbox/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  didSend: (touchId: string, sent: boolean) => post<unknown>(`/api/v1/sms/touches/${touchId}/did-send`, { sent }),
};

const GSM7 = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
);
const GSM7_EXT = new Set("^{}\\[~]|€");

/** The browser twin of api/services/sms.segments: 160/153 per segment in GSM-7, 70/67 in UCS-2. */
export function segmentCount(body: string): { chars: number; segments: number; encoding: "GSM-7" | "UCS-2" } {
  const chars = [...body];
  const gsm = chars.every((c) => GSM7.has(c) || GSM7_EXT.has(c));
  const units = gsm
    ? chars.reduce((n, c) => n + (GSM7_EXT.has(c) ? 2 : 1), 0)
    : chars.reduce((n, c) => n + (c.length > 1 ? 2 : 1), 0);
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  const segments = body.length === 0 ? 0 : units <= single ? 1 : Math.ceil(units / multi);
  return { chars: chars.length, segments, encoding: gsm ? "GSM-7" : "UCS-2" };
}

export const TEXT_STATUS_LABEL: Record<string, string> = {
  sent: "Sent",
  pending_manual: "Opened in Voice — waiting",
  unconfirmed: "Did this send?",
  failed: "Not sent",
};
